import { listUserProjectsApi } from "@/api/api";
import { defineChatTool, type ChatTool } from "@codenexus/react";
import type { AppChatEventMap } from "../chatTools/types";
import type { CurrentProductContext } from "../chatTools/createChatTools";
import type { BatchGenerationController } from "./types";

interface BatchToolArgs {
    productIds?: number[];
    productNames?: string[];
}

interface ProjectRecord {
    project_id: number;
    project_name?: string;
}

const parameters = {
    type: "object" as const,
    properties: {
        productIds: { type: "array", items: { type: "number" }, description: "Authorized product IDs, when known." },
        productNames: { type: "array", items: { type: "string" }, description: "Explicit product names from the request." }
    },
    required: [] as string[],
    additionalProperties: false
};

function matchProjects(projects: ProjectRecord[], name: string): ProjectRecord[] {
    const needle = name.trim().toLowerCase();
    const exact = projects.filter(project => (project.project_name || "").trim().toLowerCase() === needle);
    if (exact.length) return exact;
    return projects.filter(project => (project.project_name || "").trim().toLowerCase().includes(needle));
}

async function resolveTargets(
    args: BatchToolArgs,
    getToken: () => Promise<string>,
    currentModule: string,
    getCurrentProduct: () => CurrentProductContext | null
): Promise<{ productIds: number[]; products: Array<{ id: number; name: string }> }> {
    const token = await getToken();
    const { projects: rawProjects } = await listUserProjectsApi(token);
    const projects = rawProjects as unknown as ProjectRecord[];
    const resolved = new Map<number, ProjectRecord>();
    for (const rawId of args.productIds || []) {
        const id = Number(rawId);
        const match = projects.find(project => Number(project.project_id) === id);
        if (!match) throw new Error(`No authorized product matched id ${rawId}.`);
        resolved.set(id, match);
    }
    for (const name of args.productNames || []) {
        const matches = matchProjects(projects, name);
        if (matches.length === 0) throw new Error(`No product matched “${name}”.`);
        if (matches.length > 1) {
            throw new Error(`“${name}” is ambiguous. Matches: ${matches.map(project => project.project_name).join(", ")}.`);
        }
        resolved.set(Number(matches[0].project_id), matches[0]);
    }
    if (resolved.size === 0 && currentModule === "feature-studio") {
        const current = getCurrentProduct();
        const id = Number(current?.projectId);
        const match = projects.find(project => Number(project.project_id) === id);
        if (match) resolved.set(id, match);
    }
    if (resolved.size === 0) {
        throw new Error(
            currentModule === "product-hub"
                ? "Name at least one product. Product Hub batch generation never assumes the whole portfolio."
                : "Open a Feature Studio product workspace or name a product first."
        );
    }
    return {
        productIds: [...resolved.keys()],
        products: [...resolved].map(([id, project]) => ({ id, name: project.project_name || `Product ${id}` }))
    };
}

export function createBatchGenerationTools(options: {
    getToken: () => Promise<string>;
    currentModule: string;
    getCurrentProduct: () => CurrentProductContext | null;
    controller: BatchGenerationController;
}): ChatTool[] {
    const check = defineChatTool<BatchToolArgs, AppChatEventMap>({
        name: "check_batch_work_product_readiness",
        category: "read",
        description: "Authoritatively checks Context Mesh readiness for every requested product before batch generation. Always call this before generate_all_work_products.",
        parameters,
        execute: async args => {
            let targets: Awaited<ReturnType<typeof resolveTargets>>;
            try {
                targets = await resolveTargets(args, options.getToken, options.currentModule, options.getCurrentProduct);
            } catch (error) {
                const message = error instanceof Error ? error.message : "Target resolution failed.";
                options.controller.failPendingChat(message);
                return { ok: false, error: message };
            }
            try {
                const readiness = await options.controller.checkReadiness(targets.productIds);
                return {
                    ok: readiness.ready,
                    ready: readiness.ready,
                    products: readiness.products.map(product => ({
                        productId: product.productId,
                        productName: product.productName,
                        ready: product.ready,
                        missingInputs: product.missingInputs
                    })),
                    targets: targets.products
                };
            } catch (error) {
                return { ok: false, error: error instanceof Error ? error.message : "Readiness check failed." };
            }
        }
    });
    const generate = defineChatTool<BatchToolArgs, AppChatEventMap>({
        name: "generate_all_work_products",
        category: "mutation",
        description: "Starts asynchronous generation of all missing or stale work products after a successful readiness check. Product Hub requires explicit targets; Feature Studio uses the active product's complete feature tree.",
        parameters,
        execute: async args => {
            let targets: Awaited<ReturnType<typeof resolveTargets>>;
            try {
                targets = await resolveTargets(args, options.getToken, options.currentModule, options.getCurrentProduct);
            } catch (error) {
                const message = error instanceof Error ? error.message : "Target resolution failed.";
                options.controller.failPendingChat(message);
                return { ok: false, error: message };
            }
            try {
                const handle = await options.controller.startBatch({ productIds: targets.productIds, origin: "chat" });
                return {
                    ok: true,
                    ...handle,
                    progressLocation: "Background activity",
                    message: "Batch generation started. Progress is available in the Background activity tab."
                };
            } catch (error) {
                return { ok: false, error: error instanceof Error ? error.message : "Batch generation failed to start." };
            }
        }
    });
    return [check, generate] as ChatTool[];
}

const ACTION = /\b(?:generate|create|build\s+out|synthesi[sz]e|produce)\b/i;
const ALL = /\b(?:all|every|entire|full|whole|complete)\b/i;
const TARGET = /\b(?:work\s*products?|artifacts?|specs?|specifications?|stories|deliverables?)\b/i;
const LISTING = /^(?:what|which|list|show|tell\s+me)\b/i;

export function isBatchGenerationPrompt(message: string): boolean {
    const value = message.trim();
    return !LISTING.test(value) && ACTION.test(value) && ALL.test(value) && TARGET.test(value);
}
